import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';

interface TickData {
  symbol: string;
  price: number;
  ts: number;
}

function Realtime() {
  const [isConnected, setIsConnected] = useState(true); // Mock connected state
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [tickHistory, setTickHistory] = useState<TickData[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState('BTC-USD');
  const [isSimulating, setIsSimulating] = useState(false);
  const simulationRef = useRef<number | null>(null);

  const symbols = ['BTC-USD', 'ETH-USD', 'XRP-USD', 'ADA-USD', 'DOT-USD'];

  // Mock price ranges for different symbols
  const mockPriceRanges = {
    'BTC-USD': { min: 40000, max: 50000 },
    'ETH-USD': { min: 2500, max: 3500 },
    'XRP-USD': { min: 0.50, max: 0.70 },
    'ADA-USD': { min: 0.40, max: 0.60 },
    'DOT-USD': { min: 6, max: 9 }
  };

  useEffect(() => {
    // Start with some initial mock data
    generateInitialTicks();
    
    return () => {
      if (simulationRef.current) {
        clearInterval(simulationRef.current);
      }
    };
  }, [selectedSymbol]);

  const generateInitialTicks = () => {
    const range = mockPriceRanges[selectedSymbol as keyof typeof mockPriceRanges];
    const initialTicks: TickData[] = [];
    
    for (let i = 0; i < 20; i++) {
      const price = range.min + Math.random() * (range.max - range.min);
      const timestamp = Date.now() - (20 - i) * 5000; // 5 seconds apart
      
      initialTicks.push({
        symbol: selectedSymbol,
        price,
        ts: timestamp
      });
    }
    
    setTickHistory(initialTicks);
    setCurrentPrice(initialTicks[initialTicks.length - 1].price);
    setLastUpdate(new Date(initialTicks[initialTicks.length - 1].ts));
  };

  const startSimulation = () => {
    if (isSimulating) return;
    
    setIsSimulating(true);
    const range = mockPriceRanges[selectedSymbol as keyof typeof mockPriceRanges];
    
    simulationRef.current = setInterval(() => {
      const price = range.min + Math.random() * (range.max - range.min);
      const newTick: TickData = {
        symbol: selectedSymbol,
        price,
        ts: Date.now()
      };
      
      setCurrentPrice(price);
      setLastUpdate(new Date());
      setTickHistory(prev => {
        const newHistory = [...prev, newTick];
        // Keep only last 100 ticks
        return newHistory.slice(-100);
      });
    }, 2000); // New tick every 2 seconds
  };

  const stopSimulation = () => {
    if (simulationRef.current) {
      clearInterval(simulationRef.current);
      simulationRef.current = null;
    }
    setIsSimulating(false);
  };

  const publishTestTick = () => {
    const range = mockPriceRanges[selectedSymbol as keyof typeof mockPriceRanges];
    const price = range.min + Math.random() * (range.max - range.min);
    const newTick: TickData = {
      symbol: selectedSymbol,
      price,
      ts: Date.now()
    };
    
    setCurrentPrice(price);
    setLastUpdate(new Date());
    setTickHistory(prev => {
      const newHistory = [...prev, newTick];
      return newHistory.slice(-100);
    });
  };

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto p-3 md:p-6">
        <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Real-time Market Data</h1>
          <div className="flex items-center space-x-4">
            <select
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {symbols.map(symbol => (
                <option key={symbol} value={symbol}>{symbol}</option>
              ))}
            </select>
            <button
              onClick={publishTestTick}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Publish Test Tick
            </button>
            {!isSimulating ? (
              <button
                onClick={startSimulation}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Start Simulation
              </button>
            ) : (
              <button
                onClick={stopSimulation}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Stop Simulation
              </button>
            )}
          </div>
        </div>

        {/* Connection Status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full mr-3 bg-green-500"></div>
              <div>
                <p className="text-sm font-medium text-gray-600">Connection Status</p>
                <p className="font-semibold text-green-800">
                  Connected (Mock Data)
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-blue-600 text-2xl">💰</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-600">Current Price</p>
                <p className="text-2xl font-bold text-blue-900">
                  {currentPrice ? `$${currentPrice.toFixed(2)}` : '--'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-purple-600 text-2xl">🕒</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-purple-600">Last Update</p>
                <p className="text-lg font-bold text-purple-900">
                  {lastUpdate ? lastUpdate.toLocaleTimeString() : 'Never'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Price Chart (Simple) */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Price History</h3>
          {tickHistory.length > 0 ? (
            <div className="h-64 flex items-end space-x-1">
              {tickHistory.map((tick, index) => {
                const maxPrice = Math.max(...tickHistory.map(t => t.price));
                const minPrice = Math.min(...tickHistory.map(t => t.price));
                const priceRange = maxPrice - minPrice;
                const height = priceRange > 0 ? ((tick.price - minPrice) / priceRange) * 100 : 50;
                
                return (
                  <div
                    key={index}
                    className="bg-blue-500 rounded-t"
                    style={{
                      width: '4px',
                      height: `${height}%`,
                      minHeight: '2px'
                    }}
                    title={`$${tick.price.toFixed(2)} at ${new Date(tick.ts).toLocaleTimeString()}`}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500">
              No price data yet. Use "Publish Test Tick" or "Start Simulation" to see data.
            </div>
          )}
        </div>

        {/* Recent Ticks */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Ticks</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tickHistory.slice().reverse().map((tick, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(tick.ts).toLocaleTimeString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {tick.symbol}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      ${tick.price.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {tickHistory.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No tick data available. Use "Publish Test Tick" or "Start Simulation" to see data.
              </div>
            )}
          </div>
        </div>
      </div>
        </div>
      </div>
    </>
  );
}

export default Realtime;




